'use client'

import * as TogglePrimitive from '@radix-ui/react-toggle'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/utils/shadcn/utils'

const toggleVariants = cva(
  `grow
  items-center
  justify-center
  text-sm
  font-medium
  transition-colors
  hover:bg-muted
  hover:text-muted-foreground
  focus-visible:outline-hidden
  focus-visible:ring-1
  focus-visible:ring-ring
  disabled:cursor-not-allowed
  disabled:opacity-50
  data-[state=on]:bg-moment-white-100
  data-[state=on]:text-foreground
  dark:data-[state=on]:bg-moment-teal-1000
  data-[state=off]:bg-moment-white-200
  data-[state=off]:text-moment-blue-grey-800
  dark:data-[state=off]:text-moment-grey-400
  dark:data-[state=off]:bg-moment-black-100
  data-[state=off]:not([disabled]):hover:bg-moment-grey-200
  dark:data-[state=off]:not([disabled]):hover:bg-moment-grey-800
  [&>div>p]:text-moment-blue-grey-800
  dark:[&>div>p]:text-moment-grey-400
  rounded-md
  border
  border-transparent
  data-[state=on]:border-moment-blue-grey-200
  dark:data-[state=on]:border-moment-teal-900
  hover:cursor-pointer`,
  {
    variants: {
      variant: {
        default: 'bg-transparent',
      },
      size: {
        default: 'h-[28px] px-[10px] py-[2px]',
      },
      position: {
        start: 'rounded-l-md',
        middle: '',
        end: 'rounded-r-md',
        single: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      position: 'single',
    },
  },
)

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants> & { children?: React.ReactNode }
>({
  size: 'default',
  variant: 'default',
})

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn(
      `inline-flex
      w-full
      items-center
      justify-center
      rounded-md`,
      className,
    )}
    {...props}
  >
    <ToggleGroupContext.Provider value={{ variant, size, children }}>
      {children}
    </ToggleGroupContext.Provider>
  </ToggleGroupPrimitive.Root>
))

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleVariants>
>(({ className, children, variant, size, ...props }, ref) => {
  const context = React.useContext(ToggleGroupContext)
  const itemsCount = React.Children.count(context.children)
  const itemIndex = React.Children.toArray(context.children).findIndex(
    (child) =>
      (child as React.ReactElement<{ value: string }>).props.value ===
      props.value,
  )

  let position: 'start' | 'middle' | 'end' | 'single' = 'single'
  if (itemsCount > 1) {
    if (itemIndex === 0) position = 'start'
    else if (itemIndex === itemsCount - 1) position = 'end'
    else position = 'middle'
  }

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
          position,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  )
})

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName

const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TogglePrimitive.Root
    ref={ref}
    className={cn(toggleVariants({ variant, size, className }))}
    {...props}
  />
))

Toggle.displayName = TogglePrimitive.Root.displayName

export { ToggleGroup, ToggleGroupItem }
